// Copyright (c) 2024 - Present. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { EllipsisTypography } from '@/components/ellipsis-typography';
import { ProjectReportRow } from '@/report/api/types';
import { ReportTable } from '@/report/components/report-table';
import { getDates } from '@/report/utilities/get-dates';
import {
  GRID_ROW_GROUPING_SINGLE_GROUPING_FIELD,
  GridAggregationFunction,
  GridAggregationModel,
  GridColDef,
  GridGroupingColDefOverride,
  GridPinnedColumnFields,
  GridRowGroupingModel,
  isAutogeneratedRow,
  useGridApiRef,
  useKeepGroupedColumnsHidden,
} from '@mui/x-data-grid-premium';
import { sum } from 'lodash-es';
import { Interval } from 'luxon';
import { useMemo } from 'react';

type GridColumnDefinition = GridColDef<ProjectReportRow>;

// Unfortunately, Material UI does not provide a simple way to separate the
// rendering of null values from the operation of the grid's sorting algorithm.
// This constant is used to represent a null value in the 'customerName' field
// but is also used to sort the 'customerName' field to the bottom of the table,
// so it must be user-friendly.
const NO_CUSTOMER_VALUE = '[Customer not set]';
const UNIT_COUNT_AGGREGATION_NAME = 'unitCount';
const EXPORT_COLUMN_ORDER = ['customerName', 'projectName', 'productName'];
const UNIT_COUNT_AGGREGATION: GridAggregationFunction<number, number> = {
  // Unfortunately, Material UI does not provide an option to hide this label in
  // the grid's column header so an empty string is used instead.
  label: '',
  apply: ({ values }) => {
    return sum(values);
  },
  // Unfortunately, Material UI's DataGrid has very poor TypeScript support for
  // valueFormatter. It cannot infer the type of the 'value' parameter, so it
  // must be explicitly typed as a number.
  valueFormatter: (value: number) => {
    if (typeof value === 'number') {
      return value.toLocaleString();
    }

    return value;
  },
};

const AGGREGATION_FUNCTIONS: Record<string, GridAggregationFunction> = {
  [UNIT_COUNT_AGGREGATION_NAME]: UNIT_COUNT_AGGREGATION,
};

const PINNED_COLUMNS: GridPinnedColumnFields = {
  left: [GRID_ROW_GROUPING_SINGLE_GROUPING_FIELD, 'serial'],
};

const COLUMN_VISIBILITY_MODEL = {
  customerName: false,
  productName: false,
  projectName: false,
};

// The <DataGrid> always has a fixed location, product, and asset column. Date
// columns are added dynamically based on the 'interval' prop provided to the
// component.
const COLUMN_DEFINITIONS: GridColumnDefinition[] = [
  {
    width: 200,
    type: 'string',
    field: 'customerName',
    headerName: 'Customer',
    groupingValueGetter: (_, row) => {
      return row.customerName ?? NO_CUSTOMER_VALUE;
    },
    renderCell: (parameters) => {
      return (
        <EllipsisTypography variant="body2" maximumWidth={168}>
          {parameters.formattedValue}
        </EllipsisTypography>
      );
    },
  },
  {
    width: 136,
    type: 'string',
    field: 'projectName',
    headerName: 'Project',
    renderCell: (parameters) => {
      return (
        <EllipsisTypography variant="body2" maximumWidth={136}>
          {parameters.formattedValue}
        </EllipsisTypography>
      );
    },
  },
  {
    type: 'string',
    field: 'productName',
    headerName: 'Product',
    renderCell: (parameters) => {
      return (
        <EllipsisTypography
          variant="body2"
          marginLeft="auto"
          maximumWidth={200}
        >
          {parameters.formattedValue}
        </EllipsisTypography>
      );
    },
  },
];

/**
 * Creates a column definition for a specific date that corresponds to a cell
 * index in a row.
 *
 * @param date - An ISO date string representing the columns's date.
 * @param index - The index of the corresponding date cell in the row data.
 */
function createDateColumnDefinition(date: string, index: number) {
  const columnDefinition: GridColumnDefinition = {
    type: 'number',
    align: 'right',
    width: 100,
    field: date,
    sortable: false,
    headerName: date,
    valueGetter: (value, row) => {
      if (isAutogeneratedRow(row)) {
        return value;
      }

      if (index >= row.cells.length) {
        return false;
      }

      return row.cells[index].requestedUnitCount;
    },
  };

  return columnDefinition;
}

/**
 * Creates an aggregation model for the <DataGrid> component that maps each
 * date in an interval to the 'assetCount' aggregation function.
 *
 * @param interval - An Interval representing the date range.
 */
function createAggregationModel(interval: Interval) {
  const dates = getDates(interval);

  return dates.reduce<GridAggregationModel>(
    (model, date) => ({ ...model, [date]: UNIT_COUNT_AGGREGATION_NAME }),
    {}
  );
}

interface Props {
  /**
   * A collection of rows to display in the table.
   */
  data: ProjectReportRow[];

  group?: 'customer'[];

  /**
   * The interval of the report.
   */
  interval: Interval;

  /**
   * If true, the table is displayed in a loading state.
   */
  isLoading?: boolean;

  /**
   * The maximum height of the table. This is used to make the table scrollable.
   */
  maximumHeight: number;

  /**
   * The empty state message to display when there is no data.
   */
  emptyState?: string;

  /**
   * A callback fired when the user clicks the export button that returns the
   * name of the file to be exported. This file name should not include a file
   * extension.
   */
  getFileName: () => string;
}

/**
 * A data table that displays rows of asset location data over a date range.
 */
export function ProjectReportTable(props: Props) {
  const apiRef = useGridApiRef();

  const initialState = useKeepGroupedColumnsHidden({
    apiRef,
    initialState: {
      pinnedColumns: PINNED_COLUMNS,
      // The product column is hidden because it is used as the leaf node for
      // the grouping column.
      columns: { columnVisibilityModel: { productName: false } },
      sorting: {
        sortModel: [
          { field: GRID_ROW_GROUPING_SINGLE_GROUPING_FIELD, sort: 'asc' },
        ],
      },
    },
  });

  const aggregationModel = useMemo(
    () => createAggregationModel(props.interval),
    [props.interval]
  );

  const group = useMemo(() => {
    return props.group ?? ['customer'];
  }, [props.group]);

  const rowGroupingModel: GridRowGroupingModel = useMemo(() => {
    if (group.includes('customer')) {
      return ['customerName', 'projectName'];
    }

    return ['projectName'];
  }, [group]);

  const columnDefinitions = useMemo(() => {
    const dates = getDates(props.interval);

    return dates.reduce((columnDefinitions, date, index) => {
      return [...columnDefinitions, createDateColumnDefinition(date, index)];
    }, COLUMN_DEFINITIONS);
  }, [props.interval]);

  const groupingColumnDefinition: GridGroupingColDefOverride = useMemo(
    () => ({
      headerName: group.includes('customer') ? 'Customer' : 'Project',
      sortable: true,
      disableExport: true,
      hideDescendantCount: true,
      leafField: 'productName',
      mainGroupingCriteria: group.includes('customer')
        ? 'customerName'
        : 'projectName',
      sortComparator: (_value, _other, cell, other) => {
        if (cell.value === NO_CUSTOMER_VALUE) {
          return 1;
        }

        if (other.value === NO_CUSTOMER_VALUE) {
          return -1;
        }

        if (cell.rowNode.type === 'group' && other.rowNode.type === 'group') {
          return cell.value.localeCompare(other.value);
        }

        return 0;
      },
    }),
    [group]
  );

  return (
    <ReportTable
      rows={props.data}
      apiRef={apiRef}
      loading={props.isLoading}
      columns={columnDefinitions}
      initialState={initialState}
      aggregationModel={aggregationModel}
      rowGroupingModel={rowGroupingModel}
      groupingColDef={groupingColumnDefinition}
      aggregationFunctions={AGGREGATION_FUNCTIONS}
      columnVisibilityModel={COLUMN_VISIBILITY_MODEL}
      maximumHeight={props.maximumHeight}
      getFileName={props.getFileName}
      exportColumnOrder={EXPORT_COLUMN_ORDER}
      emptyState={props.emptyState}
    />
  );
}
